/**
 * 商家工作台
 * 订单只有 PAID/REFUNDED 两种状态。分账和开票在订单 PAID 且确认收货后自动完成。
 */

import { useState } from 'react'
import { Card, useApi, money, Badge, Loading, Button } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Partnership {
  partnership_id: string
  factory_id: string
  commission_rate: string
  status: string
}
interface Order {
  order_id: string
  amount_total: string
  amount_refunded: string
  status: string
  confirm_time: string | null
  logistics_no: string | null
}

export function SellerDashboard() {
  const partnerships = useApi<Partnership[]>(async () => api.get('/partnerships'), [])
  const orders = useApi<Order[]>(async () => api.get('/orders'), [])
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState('')

  const partnershipId = partnerships.data?.[0]?.partnership_id ?? 'PT-DEMO-01'
  const paidOrders = (orders.data ?? []).filter((o) => o.status === 'PAID')
  const refundedOrders = (orders.data ?? []).filter((o) => o.status === 'REFUNDED')

  async function createOrder() {
    setBusy('create'); setMsg('')
    try {
      const r = await api.post<Order>('/orders/sync', {
        partnershipId,
        amountTotal: 100,
        skuList: [{ sku_id: 'SKU-NEW', name: '测试商品', qty: 1, unit_price: 100 }],
      })
      setMsg(`✓ 已同步订单 ${r.order_id}（¥100，已支付）`)
      orders.refresh()
    } catch (e) { setMsg((e as Error).message) } finally { setBusy('') }
  }

  async function syncRefund() {
    setBusy('refund'); setMsg('')
    try {
      const r = await api.post<{ order: Order; event: string }>('/orders/sync', { simulate: 'refund' })
      setMsg(`✓ 订单 ${r.order.order_id} 已同步退款：${r.event}`)
      orders.refresh()
    } catch (e) { setMsg((e as Error).message) } finally { setBusy('') }
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>商家工作台</h2>
      {msg && <div style={{ marginBottom: 12, color: msg.startsWith('✓') ? '#16a34a' : '#dc2626', fontSize: 13 }}>{msg}</div>}

      {/* 指标卡 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
        <MetricCard label="合作关系" value={partnerships.data?.length ?? '-'} />
        <MetricCard label="生效合作" value={partnerships.data?.filter((p) => p.status === 'ACTIVE').length ?? '-'} />
        <MetricCard label="已支付订单" value={paidOrders.length} highlight />
        <MetricCard label="已退款订单" value={refundedOrders.length} />
      </div>

      {/* 订单同步入口 */}
      <Card title="订单同步（INT-A）">
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          一切业务事件从电商平台同步过来。同步订单时如果该订单在电商那边已退款，平台按状态自动处理钱和票。
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={createOrder} disabled={!!busy}>
            {busy === 'create' ? '同步中...' : '📥 同步新订单（mock，¥100 已支付）'}
          </Button>
          <Button variant="default" onClick={syncRefund} disabled={!!busy}>
            {busy === 'refund' ? '同步中...' : '同步退款（mock，最早一笔 PAID 订单）'}
          </Button>
        </div>
      </Card>

      {/* 最近订单 */}
      <Card title="最近订单">
        {orders.loading ? (
          <Loading />
        ) : (
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#666' }}>
                <th style={{ padding: 8 }}>订单号</th>
                <th>金额</th>
                <th>状态</th>
              </tr>
            </thead>
            <tbody>
              {(orders.data ?? []).slice(0, 5).map((o) => (
                <tr key={o.order_id}>
                  <td style={{ padding: 8 }}>{o.order_id}</td>
                  <td>{money(o.amount_total)}</td>
                  <td><Badge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <HelpBubble pageKey="seller-dashboard" title="商家工作台">
        <p>订单从电商平台（抖音/拼多多/快手）同步过来，只有<b>已支付</b>和<b>已退款</b>两种状态。</p>
        <p>点「同步新订单」模拟电商拉单；点「同步退款」模拟一笔订单退款。</p>
        <p>分账和开票在确认收货后<b>自动完成</b>。分账的钱来自商家充值（资金账户页），退款也从商家余额扣。</p>
      </HelpBubble>
    </div>
  )
}

function MetricCard({ label, value, highlight }: { label: string; value: number | string; highlight?: boolean }) {
  return (
    <div
      style={{
        background: '#fff',
        padding: 16,
        borderRadius: 8,
        border: highlight ? '2px solid #f59e0b' : '1px solid #e5e7eb',
      }}
    >
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: highlight ? '#d97706' : undefined }}>{value}</div>
    </div>
  )
}
